import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Info, CheckCircle2, GripVertical, Bell, MessageSquare, Mail, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type CalendarCustomFieldType = 'text' | 'address' | 'phone';

interface CalendarCustomField {
  id: string;
  label: string;
  required: boolean;
  type: CalendarCustomFieldType;
  enabled: boolean;
}

interface CalendarEventConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

const FIELD_LABELS: Record<string, string> = {
  customer_name: '👤 Nome do Cliente',
  customer_email: '📧 E-mail',
  customer_phone: '📱 Telefone',
  service_item: '🛠️ Serviço/Item'
};

const FIELD_EXAMPLES: Record<string, string> = {
  customer_name: 'João Silva',
  service_item: 'Consulta Médica',
  customer_email: 'joao@example.com',
  customer_phone: '(51) 99999-9999'
};

const CUSTOM_FIELD_TYPE_LABELS: Record<CalendarCustomFieldType, string> = {
  text: 'Texto',
  address: 'Endereço',
  phone: 'Telefone',
};

function parseCustomFieldsFromDb(raw: unknown): CalendarCustomField[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item, index) => ({
      id: String(item.id || crypto.randomUUID()),
      label: String(item.label || '').trim(),
      required: item.required === true,
      type: (['text', 'address', 'phone'].includes(String(item.type)) ? item.type : 'text') as CalendarCustomFieldType,
      enabled: item.enabled !== false,
    }))
    .filter((field) => field.label.length > 0 && field.enabled);
}

const REMINDER_TIME_OPTIONS = [
  { value: '24h', label: '24 horas antes' },
  { value: '2h', label: '2 horas antes' },
  { value: '30min', label: '30 minutos antes' },
];

const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'both', label: 'Ambos (WhatsApp + Email)', icon: Bell },
];

const DEFAULT_MESSAGE_TEMPLATE = `Olá {nome}! 📅

Lembrete: você tem um agendamento marcado para {data} às {hora}.

Serviço: {servico}

Confirme sua presença respondendo esta mensagem. Se precisar remarcar, entre em contato conosco.

Aguardamos você! 😊`;

function SortableItem({ id }: { id: string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-muted rounded-lg cursor-move"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <span>{FIELD_LABELS[id]}</span>
    </div>
  );
}

export function CalendarEventConfigDialog({ open, onOpenChange, workspaceId }: CalendarEventConfigDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [requireName, setRequireName] = useState(true);
  const [requireEmail, setRequireEmail] = useState(true);
  const [requirePhone, setRequirePhone] = useState(true);
  const [requireService, setRequireService] = useState(true);
  const [requirePresencialAddress, setRequirePresencialAddress] = useState(false);
  const [defaultLocation, setDefaultLocation] = useState("");
  const [customFields, setCustomFields] = useState<CalendarCustomField[]>([]);
  const [titleFormat, setTitleFormat] = useState<string[]>(['service_item', 'customer_name']);
  const [titleSeparator, setTitleSeparator] = useState(' - ');
  const [includeGoogleMeet, setIncludeGoogleMeet] = useState(false);
  const [modalityMode, setModalityMode] = useState<'always_online' | 'always_presencial' | 'ask_per_appointment'>('always_presencial');
  
  // Reminder settings
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTimes, setReminderTimes] = useState<string[]>(['24h', '2h']);
  const [reminderChannel, setReminderChannel] = useState('whatsapp');
  const [reminderMessageTemplate, setReminderMessageTemplate] = useState(DEFAULT_MESSAGE_TEMPLATE);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (open && workspaceId) {
      loadConfig();
    }
  }, [open, workspaceId]);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('calendar_event_configs')
        .select('*')
        .eq('workspace_id', workspaceId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setRequireName(data.require_customer_name ?? true);
        setRequireEmail(data.require_customer_email ?? true);
        setRequirePhone(data.require_customer_phone ?? true);
        setRequireService(data.require_service_item ?? true);
        setRequirePresencialAddress(data.require_presencial_address ?? false);
        setDefaultLocation(data.default_location || "");
        setCustomFields(parseCustomFieldsFromDb(data.custom_fields));
        setTitleFormat(data.title_format || ['service_item', 'customer_name']);
        setTitleSeparator(data.title_separator || ' - ');
        setIncludeGoogleMeet(data.include_google_meet ?? false);
        const persistedMode = (data as any).meeting_modality_mode as typeof modalityMode | undefined;
        setModalityMode(persistedMode || (data.include_google_meet ? 'always_online' : 'always_presencial'));
        setReminderEnabled(data.reminder_enabled ?? false);
        setReminderTimes(data.reminder_times || ['24h', '2h']);
        setReminderChannel(data.reminder_channel || 'whatsapp');
        setReminderMessageTemplate(data.reminder_message_template || DEFAULT_MESSAGE_TEMPLATE);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const configData = {
        workspace_id: workspaceId,
        require_customer_name: requireName,
        require_customer_email: requireEmail,
        require_customer_phone: requirePhone,
        require_service_item: requireService,
        require_presencial_address: requirePresencialAddress,
        default_location: defaultLocation.trim() || null,
        custom_fields: customFields.filter((field) => field.label.trim()) as any,
        title_format: titleFormat,
        title_separator: titleSeparator,
        include_google_meet: modalityMode === 'always_online',
        meeting_modality_mode: modalityMode,
        reminder_enabled: reminderEnabled,
        reminder_times: reminderTimes,
        reminder_channel: reminderChannel,
        reminder_message_template: reminderMessageTemplate,
      };

      const { error } = await supabase
        .from('calendar_event_configs')
        .upsert([configData], { onConflict: 'workspace_id' });

      if (error) throw error;

      toast({
        title: "Configurações salvas",
        description: "As configurações de eventos foram atualizadas com sucesso.",
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving config:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar as configurações.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTitleFormat((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const generateTitlePreview = () => {
    return titleFormat
      .map(field => FIELD_EXAMPLES[field] || field)
      .join(titleSeparator);
  };

  const handleReminderTimeToggle = (timeValue: string) => {
    setReminderTimes(prev => 
      prev.includes(timeValue)
        ? prev.filter(t => t !== timeValue)
        : [...prev, timeValue]
    );
  };

  const addCustomField = () => {
    setCustomFields((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        label: '',
        required: false,
        type: 'text',
        enabled: true,
      },
    ]);
  };

  const updateCustomField = (id: string, patch: Partial<CalendarCustomField>) => {
    setCustomFields((prev) => prev.map((field) => (
      field.id === id ? { ...field, ...patch } : field
    )));
  };

  const removeCustomField = (id: string) => {
    setCustomFields((prev) => prev.filter((field) => field.id !== id));
  };

  const generateMessagePreview = () => {
    return reminderMessageTemplate
      .replace(/{nome}/g, 'João Silva')
      .replace(/{data}/g, 'segunda-feira, 15 de janeiro')
      .replace(/{hora}/g, '14:30')
      .replace(/{servico}/g, 'Consulta Médica')
      .replace(/{telefone}/g, '(51) 99999-9999');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>⚙️ Configurar Eventos da Agenda</DialogTitle>
          <DialogDescription>
            Personalize como os agendamentos serão criados e quais informações coletar
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="required" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="required">Campos</TabsTrigger>
            <TabsTrigger value="format">Título</TabsTrigger>
            <TabsTrigger value="meeting">Reunião</TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-1">
              <Bell className="h-3 w-3" />
              Lembretes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="required" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Selecione quais informações o agente deve coletar antes de criar o agendamento:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="require-name">Nome do Cliente</Label>
                <Switch 
                  id="require-name"
                  checked={requireName} 
                  onCheckedChange={setRequireName} 
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="require-email">E-mail do Cliente</Label>
                <Switch 
                  id="require-email"
                  checked={requireEmail} 
                  onCheckedChange={setRequireEmail} 
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="require-phone">Telefone do Cliente</Label>
                <Switch 
                  id="require-phone"
                  checked={requirePhone} 
                  onCheckedChange={setRequirePhone} 
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="require-service">Serviço/Item Desejado</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Desligado = serviço opcional no agendamento
                  </p>
                </div>
                <Switch 
                  id="require-service"
                  checked={requireService} 
                  onCheckedChange={setRequireService} 
                />
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Campos personalizados</Label>
                  <p className="text-sm text-muted-foreground">
                    Informações extras coletadas antes do agendamento
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addCustomField}>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>

              {customFields.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum campo personalizado configurado.</p>
              ) : (
                <div className="space-y-3">
                  {customFields.map((field) => (
                    <div key={field.id} className="p-3 border rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={field.label}
                          onChange={(e) => updateCustomField(field.id, { label: e.target.value })}
                          placeholder="Rótulo do campo (ex.: CPF, Observações)"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCustomField(field.id)}
                          aria-label="Remover campo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Tipo</Label>
                          <Select
                            value={field.type}
                            onValueChange={(value: CalendarCustomFieldType) => updateCustomField(field.id, { type: value })}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(CUSTOM_FIELD_TYPE_LABELS).map(([value, label]) => (
                                <SelectItem key={value} value={value}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`required-${field.id}`}
                            checked={field.required}
                            onCheckedChange={(checked) => updateCustomField(field.id, { required: checked })}
                          />
                          <Label htmlFor={`required-${field.id}`}>Obrigatório</Label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="require-presencial-address">Exigir endereço em atendimento presencial</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Garante local definido no evento (padrão ou informado pelo cliente)
                  </p>
                </div>
                <Switch
                  id="require-presencial-address"
                  checked={requirePresencialAddress}
                  onCheckedChange={setRequirePresencialAddress}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="default-location">Endereço padrão presencial</Label>
                <Textarea
                  id="default-location"
                  value={defaultLocation}
                  onChange={(e) => setDefaultLocation(e.target.value)}
                  placeholder="Ex.: Rua Exemplo, 123 — Centro, Porto Alegre/RS"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  Usado em agendamentos presenciais e incluído na confirmação ao cliente.
                </p>
              </div>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                O agente só criará o agendamento após coletar TODOS os campos marcados como obrigatórios.
              </AlertDescription>
            </Alert>
          </TabsContent>

          <TabsContent value="format" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Escolha quais campos aparecerão no título do evento e em qual ordem:
            </p>

            <div className="space-y-2">
              <Label>Campos no Título (arraste para reordenar)</Label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={titleFormat} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {titleFormat.map((field) => (
                      <SortableItem key={field} id={field} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <div className="space-y-2">
              <Label>Separador</Label>
              <Select value={titleSeparator} onValueChange={setTitleSeparator}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" - "> - (hífen)</SelectItem>
                  <SelectItem value=" | "> | (barra)</SelectItem>
                  <SelectItem value=" • "> • (ponto)</SelectItem>
                  <SelectItem value=": ">: (dois pontos)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Preview do Título:</p>
              <p className="text-lg font-bold">{generateTitlePreview()}</p>
            </div>
          </TabsContent>

          <TabsContent value="meeting" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Como o agente deve decidir entre atendimento online e presencial?
            </p>

            <div className="space-y-2">
              {[
                {
                  value: 'always_online' as const,
                  title: 'Sempre online (Google Meet)',
                  desc: 'Todo agendamento criado pelo agente terá um link do Google Meet.',
                },
                {
                  value: 'always_presencial' as const,
                  title: 'Sempre presencial',
                  desc: 'Nenhum agendamento terá link de reunião. Ideal para clínicas, salões e atendimento no local.',
                },
                {
                  value: 'ask_per_appointment' as const,
                  title: 'Perguntar ao cliente em cada agendamento',
                  desc: 'O agente pergunta "Prefere online ou presencial?" antes de criar o evento. Se online → cria com Meet. Se presencial → cria sem link.',
                },
              ].map((opt) => {
                const selected = modalityMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setModalityMode(opt.value)}
                    className={`w-full text-left p-4 border rounded-lg transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-1 h-4 w-4 rounded-full border-2 shrink-0 ${
                          selected ? 'border-primary bg-primary' : 'border-muted-foreground'
                        }`}
                      />
                      <div>
                        <p className="font-medium">{opt.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{opt.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {modalityMode === 'ask_per_appointment' && (
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  O agente fará UMA pergunta curta antes de agendar e enviará o link do Meet apenas quando o cliente escolher online.
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>


          <TabsContent value="reminders" className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="reminder-enabled" className="text-base">Enviar Lembretes Automáticos</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Notificar clientes antes dos agendamentos
                </p>
              </div>
              <Switch 
                id="reminder-enabled"
                checked={reminderEnabled} 
                onCheckedChange={setReminderEnabled}
              />
            </div>

            {reminderEnabled && (
              <>
                <div className="space-y-3">
                  <Label>Quando enviar lembretes</Label>
                  <div className="space-y-2">
                    {REMINDER_TIME_OPTIONS.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`reminder-${option.value}`}
                          checked={reminderTimes.includes(option.value)}
                          onCheckedChange={() => handleReminderTimeToggle(option.value)}
                        />
                        <Label htmlFor={`reminder-${option.value}`} className="text-sm font-normal cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Canal de envio</Label>
                  <Select value={reminderChannel} onValueChange={setReminderChannel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNEL_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <option.icon className="h-4 w-4" />
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(reminderChannel === 'email' || reminderChannel === 'both') && (
                    <p className="text-xs text-muted-foreground">
                      ⚠️ Para enviar emails, configure a chave RESEND_API_KEY nas configurações.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Mensagem do lembrete</Label>
                  <Textarea
                    value={reminderMessageTemplate}
                    onChange={(e) => setReminderMessageTemplate(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Variáveis disponíveis: {'{nome}'}, {'{data}'}, {'{hora}'}, {'{servico}'}, {'{telefone}'}
                  </p>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">Preview da Mensagem:</p>
                  <p className="text-sm whitespace-pre-wrap">{generateMessagePreview()}</p>
                </div>

                <Alert>
                  <Bell className="h-4 w-4" />
                  <AlertDescription>
                    Os lembretes serão enviados automaticamente nos horários configurados antes de cada agendamento.
                    Se o agendamento for cancelado, os lembretes pendentes também serão cancelados.
                  </AlertDescription>
                </Alert>
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? "Salvando..." : "Salvar Configurações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
